import { statusSummary } from '@constants/applicationStatus';
import { emailWithBlockState } from '@components/BlockedAddresses/EmailBlockedChip';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import RecordActivityCard from '@components/Common/EntityWorkspace/RecordActivityCard';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import ErrorMessage from '@components/Common/ErrorMessage';
import FileUpload from '@components/Common/FileUpload/FileUpload';
import Loader from '@components/Common/Loader';
import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import LocationAutocomplete from '@components/Common/LocationAutocomplete';
import type { AddressDetails } from '@components/Common/LocationAutocomplete';
import NotesSection from '@components/Common/NotesSection';
import TopQuestionsCard from '@components/Common/TopQuestionsCard';
import RecordPickerDialog from '@components/Common/RecordPickerDialog';
import { RESUME_BUCKET, parseS3Url, resolveResumeUrl, resumeObjectUrl } from '@components/Common/resumeUrl';
import { uploadToS3Direct } from '@components/Common/directUpload';
import type { PickerRow } from '@components/Common/RecordPickerDialog';
import PhoneInput from '@components/Common/PhoneInput';
import SelectFormField from '@components/Common/SelectFormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import SkillProfilePanel from '@components/JobSeekers/SkillProfilePanel';
import type { Certification, EducationEntry, JobSeekerFormData, JobSeekerLinks, WorkHistoryEntry } from '@components/JobSeekers/types';
import EntryList from '@components/Common/EntryList';
import type { EntryField } from '@components/Common/EntryList';
import { SALARY_CURRENCIES, SALARY_FREQUENCY_OPTIONS, salaryFrequencyLabel } from '@constants/salary';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import { TOP_QUESTIONS_JOBSEEKER } from '@constants/topQuestions';
import {
  createJobApplicationMutation,
  requestRightToRepresentMutation,
  createJobSeekerEmbedding,
  createJobSeekerNote,
  deleteJobSeekerNote,
  getJobApplicationsByJobSeekerId,
  getJobSeekerById,
  jobSeekerInternalAttributesSave,
  jobSeekerLanguagesSave,
  jobSeekerScreeningAnswersSave,
  jobsForPicker,
  updateJobSeeker,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { currentUserDetails } from '@store/accessor';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Box, Button, Chip, Dialog, IconButton, TablePagination } from '@mui/material';
import axios from 'axios';
import dayjs from '@helpers/date';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ViewActivityButton from '@components/Common/EntityWorkspace/ViewActivityButton';
import './index.scss';
import { htmlToText } from '@helpers/utils';
import { LANGUAGES } from '@constants/languages';
import { formatDate } from '@helpers/date';

const TABS: WorkspaceTab[] = [
  { id: 'profile', label: 'Profile', icon: <PersonOutlineIcon /> },
  { id: 'preferences', label: 'Preferences', icon: <TuneOutlinedIcon /> },
  { id: 'skills', label: 'Skill profile', icon: <PsychologyOutlinedIcon /> },
  { id: 'applications', label: 'Applications', icon: <WorkOutlineIcon /> },
  { id: 'documents', label: 'Documents', icon: <DescriptionOutlinedIcon /> },
  { id: 'history', label: 'History', icon: <HistoryIcon /> },
];

const JOB_TYPES = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'FREELANCE', label: 'Freelance' },
  { value: 'INTERNSHIP', label: 'Internship' },
];

const MAX_RESUME_BYTES = 5 * 1024 * 1024;
const APP_PAGE_SIZE = 5;
const APP_WINDOW_DAYS = 30;
/** The embedding the matcher expects. A shorter vector is a stale record. */
/**
 * Length of a stored embedding vector.
 *
 * 256 was never right: text-embedding-3-large is requested at 1536 (matching the Python services
 * and the Atlas index). With the wrong number the "already indexed" guard below could never be
 * satisfied, so opening any candidate's workspace fired a fresh paid OpenAI embedding every single
 * time, and the Indexed chip permanently read "Not indexed".
 */
const EMBEDDING_DIMS = 1536;

const initialsOf = (name?: string) =>
  (name || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'JS';
const fmt = (d?: string) => formatDate(d);

/**
 * Last login as "3d ago".
 *
 * The backend stores a LocalDateTime with no zone, so a bare parse is read as
 * local time and a login shows hours in the future. Append Z when the string
 * carries no offset of its own.
 */
const lastLogin = (value?: string | null): string => {
  if (!value) return 'Never logged in';
  const at = dayjs.utc(/[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);
  return at.isValid() ? at.fromNow() : 'Never logged in';
};

/** Bucket name out of an S3 URL, so an old resume can be removed on replace. */
const WORK_FIELDS: EntryField<WorkHistoryEntry>[] = [
  { key: 'title', label: 'Title' },
  { key: 'company', label: 'Company' },
  { key: 'location', label: 'Location' },
  { key: 'from', label: 'From', placeholder: 'YYYY-MM' },
  { key: 'to', label: 'To', placeholder: 'YYYY-MM' },
  { key: 'isCurrent', label: 'Current job', type: 'boolean' },
];
const EDUCATION_FIELDS: EntryField<EducationEntry>[] = [
  { key: 'degree', label: 'Degree' },
  { key: 'fieldOfStudy', label: 'Field of study' },
  { key: 'institute', label: 'Institute' },
  { key: 'startYear', label: 'Start year', type: 'number' },
  { key: 'endYear', label: 'End year', type: 'number' },
];
const CERTIFICATION_FIELDS: EntryField<Certification>[] = [
  { key: 'name', label: 'Certificate' },
  { key: 'issuer', label: 'Issued by' },
  { key: 'year', label: 'Year', type: 'number' },
];
const LINK_FIELDS: Array<{ key: keyof JobSeekerLinks; label: string }> = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'website', label: 'Website' },
  { key: 'behance', label: 'Behance' },
  { key: 'googleDrive', label: 'Portfolio (Drive)' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitter', label: 'X / Twitter' },
];

/** "2021-04 to present". Words, not a dash. */
const period = (from?: string | number | null, to?: string | number | null, current?: boolean) => {
  const end = current ? 'present' : to;
  if (!from && !end) return undefined;
  return [from || '?', end || '?'].join(' to ');
};

/** Map the getJobSeekerById record onto the flat form updateJobSeeker expects. */
const mapUserToForm = (user: any): JobSeekerFormData => ({
  id: user._id || '',
  // fullName can be blank (a Google sign-up writes firstName/lastName only),
  // so compose it — the lists already do, which is why a name shows there.
  fullName:
    (user.personalDetails?.fullName || '').trim() ||
    [user.personalDetails?.firstName, user.personalDetails?.lastName].filter(Boolean).join(' ').trim(),
  firstName: user.personalDetails?.firstName || '',
  lastName: user.personalDetails?.lastName || '',
  email: user.personalDetails?.email || '',
  phone: user.personalDetails?.phone || '',
  city: user.personalDetails?.city || '',
  state: user.personalDetails?.state || '',
  zipcode: user.personalDetails?.zipCode || '',
  country: user.personalDetails?.country || '',
  locality: user.personalDetails?.locality || '',
  placeId: user.personalDetails?.placeId || '',
  lat: user.personalDetails?.lat ?? null,
  lng: user.personalDetails?.lng ?? null,
  birthdate: user.personalDetails?.birthday || '',
  gender: user.personalDetails?.gender || '',
  address: user.personalDetails?.address || '',
  jobType: user.jobPreferences?.jobType || '',
  location: user.jobPreferences?.preferredCities || [],
  preferredStates: user.jobPreferences?.preferredStates || [],
  preferredCountries: user.jobPreferences?.preferredCountries || [],
  preferredRoles: user.jobPreferences?.preferredRoles || [],
  noticePeriod: user.jobPreferences?.noticePeriod || '',
  noticePeriodDays: user.jobPreferences?.noticePeriodDays ?? null,
  preferredLocalities: user.jobPreferences?.preferredLocalities || [],
  salaryExpectation: user.jobPreferences?.salaryExpectation || '',
  salaryExpectationMin: user.jobPreferences?.salaryExpectationMin ?? null,
  salaryExpectationMax: user.jobPreferences?.salaryExpectationMax ?? null,
  salaryExpectationCurrency: user.jobPreferences?.salaryExpectationCurrency || 'INR',
  salaryExpectationFrequency: user.jobPreferences?.salaryExpectationFrequency || '',
  jobCategory: user.jobPreferences?.jobCategory || '',
  resumeUrl: user.resumeUrl || null,
  resume: '',
  yearsOfExperience: user.yearsOfExperience || 0,
  skills: (user.skills || []).filter(Boolean),
  overview: user.overview || '',
  jobSeekerDescription: user.jobSeekerDescription || '',
  currentCompany: user.currentCompany || '',
  currentLocation: user.currentLocation || '',
  currentRole: user.currentRole || '',
  currentSalary: user.currentSalary || '',
  currentSalaryAmount: user.currentSalaryAmount ?? null,
  currentSalaryCurrency: user.currentSalaryCurrency || '',
  currentSalaryFrequency: user.currentSalaryFrequency || '',
  workHistory: (user.workHistory || []).map((w: Partial<WorkHistoryEntry>) => ({
    company: w.company || '', title: w.title || '', location: w.location || '',
    from: w.from || '', to: w.to || '', isCurrent: Boolean(w.isCurrent),
  })),
  educationEntries: (user.educationEntries || []).map((e: Partial<EducationEntry>) => ({
    degree: e.degree || '', fieldOfStudy: e.fieldOfStudy || '', institute: e.institute || '',
    startYear: e.startYear ?? null, endYear: e.endYear ?? null,
  })),
  certifications: (user.certifications || []).map((c: Partial<Certification>) => ({
    name: c.name || '', issuer: c.issuer || '', year: c.year ?? null,
  })),
  links: {
    linkedin: user.links?.linkedin || '', instagram: user.links?.instagram || '',
    website: user.links?.website || '', behance: user.links?.behance || '',
    googleDrive: user.links?.googleDrive || '', facebook: user.links?.facebook || '',
    twitter: user.links?.twitter || '',
  },
  /*
   * Read-only here. A candidate's uploaded work is theirs to curate, and an operator editing it
   * would be changing someone's portfolio on their behalf. Rows with no usable image are dropped
   * rather than rendered as broken tiles.
   */
  portfolio: (user.portfolio || [])
    .filter((p: { imageUrl?: string }) => typeof p?.imageUrl === 'string' && p.imageUrl.trim() !== '')
    .map((p: { imageUrl: string; caption?: string; uploadedAt?: string }) => ({
      imageUrl: p.imageUrl,
      caption: p.caption || '',
      uploadedAt: p.uploadedAt || '',
    })),
  experience: user.experience || '',
  subIndustry: user.subIndustry || '',
  industry: user.industry || '',
  education: user.education || '',
  embedding: { vectorV2: user.embedding?.vectorV2 || null },
  notes: [...(user.notes || [])]
    .sort((a: any, b: any) => (new Date(b?.date || 0).getTime() || 0) - (new Date(a?.date || 0).getTime() || 0))
    .map((n: any, i: number) => ({
      id: n.id ?? String(i),
      changedBy: n.changedBy,
      note: n.note,
      date: n.date,
    })),
  isExternal: user.isExternal || false,
  lastLoginAt: user.lastLoginAt || null,
  createdAt: user.createdAt || '',
  createdBy: user.createdBy || '',
  updatedAt: user.updatedAt || '',
  updatedBy: user.updatedBy || '',
});

const JobSeekerWorkspace: React.FC = () => {
  const { jobSeekerId } = useParams<{ jobSeekerId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [form, setForm] = useState<JobSeekerFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* In the URL, not in state: a tab is a place, and this one was unshareable and did
     not survive a reload. See useWorkspaceTab. */
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'profile');
  const [editing, setEditing] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const snapshot = useRef<JobSeekerFormData | null>(null);
  const [activityOpen, toggleActivity] = useActivityRail();

  // Chip-editor drafts, one per list.
  const [skillDraft, setSkillDraft] = useState('');
  const [cityDraft, setCityDraft] = useState('');
  const [roleDraft, setRoleDraft] = useState('');

  // Resume
  const [resumeFile, setResumeFile] = useState<File | ''>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Presigned inline URL for the resume (forces render, not download).
  const [inlineResumeUrl, setInlineResumeUrl] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [applications, setApplications] = useState<any[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appPage, setAppPage] = useState(0);

  // "Submit candidate to a demand" picker
  const [submitOpen, setSubmitOpen] = useState(false);
  const [pickerJobs, setPickerJobs] = useState<
    Array<{ _id: string; title?: string; companyName?: string; city?: string; hiringMode?: string }>
  >([]);
  // Search text and selection now live in RecordPickerDialog, which owns them for both pickers.
  const [submitting, setSubmitting] = useState(false);

  // Candidate-level HR-only custom attributes (e.g. religion)
  const [internalAttrs, setInternalAttrs] = useState<Array<{ key: string; value: string }>>([]);
  /*
   * Languages, kept separate from internalAttrs above rather than being another row in that bag.
   *
   * Its save is a separate mutation for the same reason: `saveInternalAttrs` replaces the WHOLE
   * attributes list, so anything sharing that call can be dropped by an unrelated edit to a
   * free-text row. This field answers a client brief -- losing it silently because somebody
   * tidied a "Religion" row is not a trade worth making.
   */
  const [languages, setLanguages] = useState<string[]>([]);
  const [savingLanguages, setSavingLanguages] = useState(false);
  const [savingAttrs, setSavingAttrs] = useState(false);

  // The five fixed screening answers. Separate from internalAttrs above: that card replaces its
  // whole list on save, so fixed questions sharing it could be dropped by an unrelated edit.
  const [screening, setScreening] = useState<Array<{ key: string; value: string }>>([]);
  const [savingScreening, setSavingScreening] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(getJobSeekerById(id));
      if (!data?.getJobSeekerById) {
        setError('Job seeker not found.');
        return;
      }
      setForm(mapUserToForm(data.getJobSeekerById));
      setScreening(
        (data.getJobSeekerById.screeningAnswers ?? []).map((a: { key?: string; value?: string }) => ({
          key: a.key ?? '',
          value: a.value ?? '',
        })),
      );
      setInternalAttrs(
        (data.getJobSeekerById.internalAttributes ?? []).map((a: { key?: string; value?: string }) => ({
          key: a.key ?? '',
          value: a.value ?? '',
        })),
      );
      setLanguages(data.getJobSeekerById.languages ?? []);
    } catch {
      setError('Failed to load the job seeker. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jobSeekerId) load(jobSeekerId);
  }, [jobSeekerId, load]);

  /* Extracted from the mount effect so a submit can refresh it too: this list is what tells the
     picker which jobs the candidate is already on, and it was loaded exactly once. */
  const loadApplications = useCallback(async (id: string) => {
    if (!id) return;
    setAppsLoading(true);
    try {
      const { data } = await fetchGraphQLApiData(getJobApplicationsByJobSeekerId(id));
      setApplications(data?.getAllJobApplications?.content ?? []);
    } catch {
      // Not critical to the record; the section shows its empty state.
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!jobSeekerId) return;
    loadApplications(jobSeekerId);
  }, [jobSeekerId, loadApplications]);

  // Resolve a presigned inline URL when the preview opens, so a PDF renders in the
  // frame instead of downloading (resume objects are stored with attachment disposition).
  useEffect(() => {
    if (!previewOpen || !previewUrl || !/\.pdf(\?|#|$)/i.test(previewUrl)) {
      setInlineResumeUrl(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        /*
         * Through the shared parser, not a local one.
         *
         * This built the key with `pathname.replace(/^\//, '')`, which leaves it percent-encoded,
         * and then encodeURIComponent'd it again. A résumé called "RESUME 2024.pdf" is stored as
         * `RESUME%202024.pdf` and was sent as `RESUME%25202024.pdf` -- a key that does not exist,
         * so the presign resolved to nothing. Real uploads have spaces in their names, so this
         * failed for exactly the files whose names look most ordinary.
         *
         * parseS3Url decodes before re-encoding, and resolveResumeUrl wraps the whole round trip.
         */
        const signed = await resolveResumeUrl(previewUrl);
        if (active) setInlineResumeUrl(signed);
      } catch {
        if (active) setInlineResumeUrl(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [previewOpen, previewUrl]);

  /**
   * Index the candidate in the background when there is text to index and no
   * usable vector. Silent on failure: matching degrades, the record does not.
   *
   * Externals are deliberately excluded. An embedding is a 1536-float vector plus its Atlas index
   * entry, per candidate, and an imported profile usually exists to be submitted to one job by an
   * HR who already knows who they are — it does not need to be findable by similarity search.
   * Skipping them saves both OpenAI spend and Mongo storage. They become embeddable the moment
   * they claim the account, at which point they are a real user who should rank.
   */
  useEffect(() => {
    if (!jobSeekerId || !form?.overview?.trim()) return;
    if (form.isExternal) return;
    // vectorV2 is the only vector anything writes. This guard preferred it already, but neither
    // the query nor the form mapping ever supplied it -- both asked for `vector`, the 256-dim
    // legacy field -- so the length test compared 256 against 1536, never matched, and opening
    // any candidate's workspace fired a fresh paid OpenAI embedding for a record that was
    // already indexed. The legacy field is gone now; there is nothing to fall back to.
    const vector = form.embedding?.vectorV2;
    if (Array.isArray(vector) && vector.length === EMBEDDING_DIMS) return;
    (async () => {
      try {
        const { data } = await fetchGraphQLApiData(createJobSeekerEmbedding(jobSeekerId));
        if (data?.createJobSeekerEmbedding?._id) await load(jobSeekerId);
      } catch {
        // leave the record as it is
      }
    })();
    // Only the identity of the record should retrigger this, not every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobSeekerId, form?.id]);

  const setField = <K extends keyof JobSeekerFormData>(key: K, value: JobSeekerFormData[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  const startEdit = (id: string) => {
    if (editing && editing !== id && dirty) return; // one section at a time
    snapshot.current = form ? { ...form } : null;
    setEditing(id);
    setDirty(false);
  };

  const cancelEdit = () => {
    if (snapshot.current) setForm(snapshot.current);
    setResumeFile('');
    setEditing(null);
    setDirty(false);
  };

  /**
   * One save path for every section, because the mutation takes the whole
   * record. The resume is uploaded first so the mutation has its URL, and the
   * previous object is removed only once the replacement is in place.
   */
  const saveSection = async () => {
    if (!form) return;
    if (!form.firstName.trim()) return showToast('First name is required', 'error');
    if (!form.email.trim()) return showToast('Email is required', 'error');

    setSaving(true);
    try {
      let resumeUrl = form.resumeUrl;
      if (resumeFile instanceof File) {
        const fileName = `${form.id}_${Date.now()}_${resumeFile.name}`;
        // Direct-to-S3 presigned POST. The résumé bucket is private (the policy decides, not the
        // client), read back through a presigned URL; the stored URL is built from the server's key.
        const key = await uploadToS3Direct(RESUME_BUCKET, resumeFile, { fileName });
        resumeUrl = resumeObjectUrl(key);
      }

      const previous = snapshot.current?.resumeUrl;
      if (previous && previous !== resumeUrl) {
        // parseS3Url rather than splitting on '/', because the stored URL now percent-encodes
        // its key and the object is not called %20. It also reads the older raw rows, which is
        // the whole reason it exists.
        const old = parseS3Url(previous);
        if (old) {
          await axios
            .delete(`/api/s3/buckets/${old.bucket}/objects/${encodeURIComponent(old.key)}`)
            .catch(() => undefined); // a stale object must not block the save
        }
      }

      await fetchGraphQLApiData(updateJobSeeker({ ...form, resumeUrl }), {
        showStatus: true,
        message: 'Job seeker updated',
      });
      setResumeFile('');
      setEditing(null);
      setDirty(false);
      await load(form.id);
    } catch {
      showToast('Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await fetchGraphQLApiData(`mutation { deleteJobSeeker(_id: "${form.id}") { _id } }`, {
        showStatus: true,
        message: 'Deleting job seeker',
      });
      setConfirmDelete(false);
      navigate('/jobseekers');
    } catch {
      showToast('Failed to delete the job seeker', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (html: string) => {
    if (!form || !html.trim()) return;
    try {
      await fetchGraphQLApiData(createJobSeekerNote(form.id, html.trim()), {
        showStatus: true,
        message: 'Adding note',
      });
      await load(form.id);
    } catch {
      showToast('Failed to save the note', 'error');
    }
  };

  const removeNote = async (noteId: string) => {
    if (!form || !noteId) return;
    try {
      await fetchGraphQLApiData(deleteJobSeekerNote(form.id, noteId), {
        showStatus: true,
        message: 'Deleting note',
      });
      await load(form.id);
    } catch {
      showToast('Failed to delete the note', 'error');
    }
  };

  /** Resumes live in a private bucket, so every read needs a signed URL. */
  const signedResumeUrl = async (raw: string): Promise<string | null> => {
    try {
      const url = new URL(raw);
      const bucket = url.hostname.split('.')[0];
      const file = decodeURIComponent(url.pathname.split('/').pop() || '');
      const { data } = await axios.get(`/api/s3/buckets/${bucket}/download/${encodeURIComponent(file)}`);
      return data.url ?? null;
    } catch {
      return null;
    }
  };

  const downloadResume = async () => {
    if (!form?.resumeUrl) return;
    const signed = await signedResumeUrl(form.resumeUrl);
    if (signed) window.open(signed, '_blank', 'noopener,noreferrer');
    else showToast('Could not open the resume', 'error');
  };

  const openPreview = async () => {
    if (!form?.resumeUrl) return;
    setPreviewLoading(true);
    const signed = await signedResumeUrl(form.resumeUrl);
    setPreviewLoading(false);
    if (!signed) return showToast('Could not load the resume preview', 'error');
    setPreviewUrl(signed);
    setPreviewOpen(true);
  };

  const timeline: TimelineEntry[] = useMemo(() => {
    if (!form) return [];
    const t: TimelineEntry[] = (form.notes ?? []).map((n, i) => ({
      id: `n-${i}`,
      kind: 'note' as const,
      text: 'Note added',
      body: htmlToText(n.note),
      meta: `${fmt(n.date)} · ${n.changedBy || 'Unknown'}`,
    }));
    if (form.createdAt) {
      t.push({
        id: 'created',
        kind: 'submitted',
        text: 'Profile created',
        meta: `${fmt(form.createdAt)} · ${form.createdBy || 'System'}`,
      });
    }
    return t;
  }, [form]);

  /*
   * Jobs this candidate is already on.
   *
   * Built from `applications`, which the page has loaded since mount and whose query already
   * selects `jobId` — no extra request. The server refuses a duplicate now, but finding that out
   * by being rejected is a worse experience than seeing the row is already taken, and an HR
   * scanning a picker has no other way to know.
   */
  const appliedJobIds = useMemo(
    () => new Set(applications.map((a) => a?.jobId).filter(Boolean)),
    [applications],
  );

  /*
   * Rows for the job picker.
   *
   * Filtering stays in memory here, unlike the candidate picker on the job side, because the
   * source is already a capped list: jobsForPicker fetches the 100 newest jobs once. That cap is
   * a real limitation — the 101st job is unreachable no matter what is typed — but it predates
   * this change and narrowing it is a separate piece of work.
   */
  const searchJobs = useCallback(
    async (query: string): Promise<PickerRow[]> => {
      const q = query.trim().toLowerCase();
      const list = q
        ? pickerJobs.filter((j) =>
            `${j.title ?? ''} ${j.companyName ?? ''}`.toLowerCase().includes(q))
        : pickerJobs;
      // Managed demands first — that's what an HR is usually submitting against.
      return [...list]
        .sort(
          (a, b) =>
            (a.hiringMode === 'MANAGED' ? 0 : 1) - (b.hiringMode === 'MANAGED' ? 0 : 1),
        )
        .map((j) => ({
          id: j._id,
          title: j.title || 'Untitled',
          subtitle: [
            [j.companyName, j.city].filter(Boolean).join(' · '),
            j.hiringMode === 'MANAGED' ? 'Premium Hiring' : '',
          ]
            .filter(Boolean)
            .join(' · '),
          disabledReason: appliedJobIds.has(j._id) ? 'Already submitted' : undefined,
        }));
    },
    [pickerJobs, appliedJobIds],
  );

  if (loading && !form) return <SkeletonLoader variant="workspace" />;
  if (error && !form) return <ErrorMessage page message={error} />;
  if (!form) return null;

  const isEditing = (id: string) => editing === id;

  const input = (key: keyof JobSeekerFormData, type = 'text') => (
    <WorkspaceInput
      type={type}
      value={form[key] as string | number}
      onChange={(v) => setField(key, (type === 'number' ? Number(v) : v) as never)}
    />
  );

  // Cleared to null rather than to 0: an empty salary field means "not stated", and 0 would
  // publish an expectation the candidate never gave.
  const numberInput = (
    key: 'salaryExpectationMin' | 'salaryExpectationMax' | 'currentSalaryAmount' | 'noticePeriodDays',
    helper: string,
  ) => (
    <WorkspaceInput
      type="number"
      helperText={helper}
      value={form[key]}
      onChange={(v) => setField(key, v === '' ? null : Number(v))}
    />
  );

  /** Chip list with add and remove, used for skills, cities and roles. */
  const chipEditor = (
    key: 'skills' | 'location' | 'preferredRoles',
    sectionId: string,
    draft: string,
    setDraft: (v: string) => void,
    placeholder: string,
  ) => {
    const items = (form[key] ?? []).filter(Boolean);
    const add = () => {
      const v = draft.trim();
      if (v && !items.includes(v)) setField(key, [...items, v]);
      setDraft('');
    };
    return (
      <Box sx={{ gridColumn: '1 / -1' }}>
        <Box className="cc-js__chips">
          {items.length === 0 && <span className="cc-field__value">-</span>}
          {items.map((item) => (
            <Chip
              key={item}
              label={item}
              size="small"
              onDelete={isEditing(sectionId) ? () => setField(key, items.filter((s) => s !== item)) : undefined}
            />
          ))}
        </Box>
        {isEditing(sectionId) && (
          <Box className="cc-js__chip-add">
            <WorkspaceInput
              placeholder={placeholder}
              value={draft}
              onChange={setDraft}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                add();
              }}
            />
            <Button size="small" onClick={add}>Add</Button>
          </Box>
        )}
      </Box>
    );
  };

  const glance: GlanceRow[] = [
    { key: 'Experience', value: form.yearsOfExperience ? `${form.yearsOfExperience} yrs` : '-' },
    { key: 'Current role', value: form.currentRole || '-' },
    { key: 'Location', value: [form.city, form.state].filter(Boolean).join(', ') || '-' },
    { key: 'Last login', value: lastLogin(form.lastLoginAt), mono: true },
  ];

  const saveScreening = async (answers: Array<{ key: string; value: string }>) => {
    if (!form) return;
    setSavingScreening(true);
    try {
      await fetchGraphQLApiData(jobSeekerScreeningAnswersSave(form.id, answers), {
        showStatus: true,
        message: 'Top 5 questions saved',
      });
      setScreening(answers);
    } catch {
      setError('Failed to save the screening answers. Please try again.');
    } finally {
      setSavingScreening(false);
    }
  };

  const saveInternalAttrs = async () => {
    if (!form) return;
    setSavingAttrs(true);
    try {
      await fetchGraphQLApiData(
        jobSeekerInternalAttributesSave(form.id, internalAttrs.filter((a) => a.key.trim())),
        { showStatus: true, message: 'Internal fields saved' },
      );
    } catch {
      setError('Failed to save internal fields. Please try again.');
    } finally {
      setSavingAttrs(false);
    }
  };

  const saveLanguages = async () => {
    if (!form) return;
    setSavingLanguages(true);
    try {
      await fetchGraphQLApiData(jobSeekerLanguagesSave(form.id, languages), {
        showStatus: true,
        message: 'Languages saved',
      });
    } catch {
      setError('Failed to save languages. Please try again.');
    } finally {
      setSavingLanguages(false);
    }
  };

  const openSubmitDialog = async () => {
    setSubmitOpen(true);
    // The dialog renders its own "Searching…" state, so no loading flag is needed here.
    if (pickerJobs.length === 0) {
      try {
        const { data } = await fetchGraphQLApiData(jobsForPicker());
        setPickerJobs(data?.getAllJobs?.content ?? []);
      } catch {
        setError('Failed to load jobs. Please try again.');
      }
    }
  };

  const doSubmitToJob = async (selectedJobId: string) => {
    if (!form || !selectedJobId) return;
    setSubmitting(true);
    try {
      const createdBy = currentUserDetails()?.email ?? '';
      /*
       * Right to Represent: ask the candidate before the client sees them.
       *
       * The row is created as "Awaiting consent" and the candidate is emailed. The employer
       * cannot see an application in that state -- the filter is server-imposed in
       * JobApplicationsService, not here -- so nothing reaches the client until the candidate has
       * answered. Confirming moves it to Submitted and fires the usual notifications.
       */
      const created = await fetchGraphQLApiData(
        createJobApplicationMutation(form.id, selectedJobId, createdBy, 'Qualified'),
      );
      const applicationId = created?.data?.createJobApplication?._id;
      if (!applicationId) {
        throw new Error('The application was not created, so no consent request was sent.');
      }
      await fetchGraphQLApiData(requestRightToRepresentMutation(applicationId), {
        showStatus: true,
        message: 'Consent requested. We will submit once the candidate confirms.',
      });
      // No embedding for an external candidate. This used to generate one here so they could be
      // recommended for future roles; that is now a deliberate non-goal. Submitting somebody to a
      // job does not need a vector, and this path is precisely the case where an HR has already
      // found them by hand — paying to make them discoverable by similarity search afterwards is
      // the cost this change exists to remove.
      setSubmitOpen(false);
      // The picker greys out jobs this candidate is already on, and that set comes from
      // `applications` — which was loaded once at mount. Without this the row they just submitted
      // stays selectable until a reload.
      await loadApplications(form.id);
    } catch (e: any) {
      /*
       * Say which failure happened. This read "They may already be on this job" for every error,
       * guessing at a cause it had not checked, so a network failure and a genuine duplicate were
       * reported identically — and the guess was usually wrong, because nothing enforced
       * uniqueness at the time it was written.
       */
      const message = String(e?.message ?? '');
      setError(
        message.includes('already been submitted')
          ? 'This candidate is already on that job.'
          : 'Failed to submit the candidate. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const identityRail = (
    <IdentityRail
      initials={initialsOf(form.fullName)}
      name={form.fullName || 'Job seeker'}
      subId={form.id}
      statusChip={form.isExternal ? <Chip label="External" size="small" /> : undefined}
      secondaryActions={
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={<WorkOutlineIcon fontSize="small" />}
            onClick={openSubmitDialog}
          >
            Submit to a job
          </Button>
          <ViewActivityButton actorId={form.id} />
        </>
      }
      glance={glance}
      audit={{
        createdAt: form.createdAt,
        createdBy: form.createdBy,
        updatedAt: form.updatedAt,
        updatedBy: form.updatedBy,
      }}
    />
  );

  /* ---- Profile ---- */

  const profile = (
    <>
      <TopQuestionsCard
        title="Top 5 Questions"
        questions={TOP_QUESTIONS_JOBSEEKER}
        answers={screening}
        onSave={saveScreening}
        saving={savingScreening}
      />
      <SectionCard
        title="Internal fields"
        icon={<BadgeOutlinedIcon />}
        meta="HR only — shown on every application, hidden from the candidate"
      >
        <Box sx={{ gridColumn: '1 / -1' }}>
          {/*
            * Languages sit above the free-form rows because they are the one thing here with a
            * fixed vocabulary. `freeSolo` is off deliberately: this field is filtered by matching
            * the stored value, so "Gujarati" and "Gujrati" would be two different answers to the
            * same question and one of them finds nobody. Free text is exactly how salaryFrequency
            * ended up with three spellings of "monthly".
            *
            * The ROW spans every column; the FIELD does not. AutocompleteFormField is
            * fullWidth by design, so dropping it straight into a `1 / -1` cell ran a 23-item
            * closed list edge to edge across the card while every neighbouring field sat in one
            * ~190px column. `cc-field-row__grow` is the cap the repo already has for this, and
            * its own comment says so: "capped so a lone input does not run the width of a
            * workspace card". The row also aligns the button to the bottom of the input, where
            * it was previously right-aligned on a line of its own.
            */}
          <Box className="cc-field-row">
            <Box className="cc-field-row__grow">
              <AutocompleteFormField
                multiple
                freeSolo={false}
                required={false}
                label="Languages"
                name="languages"
                options={LANGUAGES}
                value={languages}
                onChange={(_e, v) => setLanguages((v as string[]) ?? [])}
              />
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={saveLanguages}
              disabled={savingLanguages}
            >
              {savingLanguages ? 'Saving…' : 'Save languages'}
            </Button>
          </Box>
          {internalAttrs.length === 0 && (
            <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--tx-mid)' }}>
              No internal fields. Add candidate attributes like Religion or Community —
              visible only to HRs and Super Admins, on every application.
            </div>
          )}
          {internalAttrs.map((row, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <WorkspaceInput
                flex
                placeholder="Field (e.g. Religion)"
                value={row.key}
                onChange={(v) =>
                  setInternalAttrs((r) => r.map((x, idx) => (idx === i ? { ...x, key: v } : x)))
                }
              />
              <WorkspaceInput
                flex
                placeholder="Value"
                value={row.value}
                onChange={(v) =>
                  setInternalAttrs((r) => r.map((x, idx) => (idx === i ? { ...x, value: v } : x)))
                }
              />
              <IconButton
                size="small"
                onClick={() => setInternalAttrs((r) => r.filter((_, idx) => idx !== i))}
                sx={{ color: 'var(--red)' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button size="small" onClick={() => setInternalAttrs((r) => [...r, { key: '', value: '' }])}>
              + Add field
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveInternalAttrs}
              disabled={savingAttrs}
            >
              {savingAttrs ? 'Saving…' : 'Save internal fields'}
            </Button>
          </Box>
        </Box>
      </SectionCard>

      <SectionCard
        title="Personal details"
        icon={<PersonOutlineIcon />}
        meta="7 fields"
        editing={isEditing('personal')}
        onEdit={() => startEdit('personal')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="First name" value={form.firstName} editing={isEditing('personal')} edit={input('firstName')} />
        <WorkspaceField label="Last name" value={form.lastName} editing={isEditing('personal')} edit={input('lastName')} />
        <WorkspaceField label="Email" value={emailWithBlockState(form.email)} mono editing={isEditing('personal')} edit={input('email')} />
        <WorkspaceField
          label="Phone"
          value={form.phone}
          mono
          editing={isEditing('personal')}
          edit={<PhoneInput fullWidth value={form.phone} onChange={(v) => setField('phone', v)} />}
        />
        {/* Read-only: the update mutation carries no birthday field, so an
            editable control here would silently discard what was typed. */}
        <WorkspaceField label="Date of birth" value={fmt(form.birthdate)} mono />
        <WorkspaceField label="Gender" value={form.gender} editing={isEditing('personal')} edit={input('gender')} />
        <WorkspaceField label="Street address" full value={form.address} editing={isEditing('personal')} edit={input('address')} />
      </SectionCard>

      <SectionCard
        title="Address"
        icon={<PlaceOutlinedIcon />}
        editing={isEditing('address')}
        onEdit={() => startEdit('address')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        {isEditing('address') ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <LocationAutocomplete
              city={form.city}
              state={form.state}
              country={form.country}
              locality={form.locality}
              zipCode={form.zipcode}
              onChange={(name, value) => setField(name, value)}
              onAddressChange={(d: AddressDetails) =>
                setForm((prev) =>
                  prev
                    ? {
                      ...prev,
                      locality: d.locality,
                      city: d.city,
                      state: d.state,
                      country: d.country,
                      zipcode: d.zipCode || prev.zipcode,
                      placeId: d.placeId,
                      lat: d.lat,
                      lng: d.lng,
                    }
                    : prev,
                )
              }
            />
          </Box>
        ) : (
          <>
            <WorkspaceField label="Locality" value={form.locality} />
            <WorkspaceField label="City" value={form.city} />
            <WorkspaceField label="State" value={form.state} />
            <WorkspaceField label="Country" value={form.country} />
            <WorkspaceField label="ZIP code" value={form.zipcode} mono />
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Professional"
        icon={<BadgeOutlinedIcon />}
        meta="9 fields"
        editing={isEditing('professional')}
        onEdit={() => startEdit('professional')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="Total experience" value={form.yearsOfExperience ? `${form.yearsOfExperience} yrs` : '-'} editing={isEditing('professional')} edit={input('yearsOfExperience', 'number')} />
        <WorkspaceField label="Current employer" value={form.currentCompany} editing={isEditing('professional')} edit={input('currentCompany')} />
        <WorkspaceField label="Current designation" value={form.currentRole} editing={isEditing('professional')} edit={input('currentRole')} />
        <WorkspaceField label="Current location" value={form.currentLocation} editing={isEditing('professional')} edit={input('currentLocation')} />
        <WorkspaceField label="Current CTC" value={form.currentSalary} mono editing={isEditing('professional')} edit={input('currentSalary')} />
        <WorkspaceField label="Experience summary" value={form.experience} editing={isEditing('professional')} edit={input('experience')} />
        <WorkspaceField label="Industry" value={form.industry} editing={isEditing('professional')} edit={input('industry')} />
        <WorkspaceField label="Sub industry" value={form.subIndustry} editing={isEditing('professional')} edit={input('subIndustry')} />
        <WorkspaceField label="Education" value={form.education} editing={isEditing('professional')} edit={input('education')} />
      </SectionCard>

      {/* Extracted from every résumé and, until now, shown nowhere. */}
      <SectionCard
        title="Summary"
        icon={<SubjectOutlinedIcon />}
        editing={isEditing('summary')}
        onEdit={() => startEdit('summary')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField
          label="Profile summary"
          full
          value={form.overview}
          editing={isEditing('summary')}
          edit={(
            <WorkspaceInput
              minRows={4}
              value={form.overview}
              onChange={(v) => setField('overview', v)}
            />
          )}
        />
      </SectionCard>

      <SectionCard
        title="Work history"
        icon={<WorkHistoryOutlinedIcon />}
        meta={`${form.workHistory.length} ${form.workHistory.length === 1 ? 'job' : 'jobs'}`}
        editing={isEditing('work')}
        onEdit={() => startEdit('work')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <EntryList<WorkHistoryEntry>
          entries={form.workHistory}
          editing={isEditing('work')}
          fields={WORK_FIELDS}
          onChange={(rows) => setField('workHistory', rows)}
          summary={(w) => ({
            title: w.title || 'Untitled role',
            subtitle: [w.company, w.location].filter(Boolean).join(' · ') || undefined,
            meta: period(w.from, w.to, w.isCurrent),
          })}
          blank={() => ({ company: '', title: '', location: '', from: '', to: '', isCurrent: false })}
          addLabel="Add job"
          emptyLabel="No work history recorded."
          itemLabel="Job"
        />
      </SectionCard>

      <SectionCard
        title="Education & certificates"
        icon={<SchoolOutlinedIcon />}
        editing={isEditing('education')}
        onEdit={() => startEdit('education')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="Qualifications" full value={null} />
        <EntryList<EducationEntry>
          entries={form.educationEntries}
          editing={isEditing('education')}
          fields={EDUCATION_FIELDS}
          onChange={(rows) => setField('educationEntries', rows)}
          summary={(e) => ({
            title: e.degree || 'Qualification',
            subtitle: [e.fieldOfStudy, e.institute].filter(Boolean).join(' · ') || undefined,
            meta: period(e.startYear, e.endYear),
          })}
          blank={() => ({ degree: '', fieldOfStudy: '', institute: '', startYear: null, endYear: null })}
          addLabel="Add qualification"
          emptyLabel="No qualifications recorded."
          itemLabel="Qualification"
        />
        <WorkspaceField label="Certificates" full value={null} />
        <EntryList<Certification>
          entries={form.certifications}
          editing={isEditing('education')}
          fields={CERTIFICATION_FIELDS}
          onChange={(rows) => setField('certifications', rows)}
          summary={(c) => ({
            title: c.name || 'Certificate',
            subtitle: c.issuer || undefined,
            meta: c.year ? String(c.year) : undefined,
          })}
          blank={() => ({ name: '', issuer: '', year: null })}
          addLabel="Add certificate"
          emptyLabel="No certificates recorded."
          itemLabel="Certificate"
        />
      </SectionCard>

      <SectionCard
        title="Links"
        icon={<LinkOutlinedIcon />}
        editing={isEditing('links')}
        onEdit={() => startEdit('links')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        {LINK_FIELDS.map(({ key, label }) => (
          <WorkspaceField
            key={key}
            label={label}
            mono
            value={form.links[key]}
            editing={isEditing('links')}
            edit={(
              <WorkspaceInput
                value={form.links[key]}
                onChange={(v) => setField('links', { ...form.links, [key]: v })}
              />
            )}
          />
        ))}
      </SectionCard>

      {/*
        * Uploaded work: designs, renders, photographs of finished pieces.
        *
        * The Control Center always calls as `admin`, so the backend's visibility rule returns the
        * field here without an entitlement lookup. That rule is a denylist plus one un-redaction
        * on getJobSeekerById, and it is the reason an employer who has never received an
        * application from this candidate sees nothing: do not re-check it here, because a second
        * opinion on the same question is a second place to get it wrong.
        */}
      {(form.portfolio?.length ?? 0) > 0 && (
        <SectionCard title="Work samples" icon={<CollectionsOutlinedIcon />} meta={`${form.portfolio?.length ?? 0}`}>
          <Box className="cc-js__portfolio">
            {(form.portfolio ?? []).map((piece, i) => (
              <figure className="cc-js__portfolio-item" key={piece.imageUrl}>
                <a href={piece.imageUrl} target="_blank" rel="noopener noreferrer">
                  <img
                    src={piece.imageUrl}
                    alt={piece.caption || `Work sample ${i + 1}`}
                    className="cc-js__portfolio-img"
                    loading="lazy"
                  />
                </a>
                {piece.caption && <figcaption>{piece.caption}</figcaption>}
              </figure>
            ))}
          </Box>
        </SectionCard>
      )}

      <SectionCard title="Danger zone" icon={<DeleteOutlineIcon />}>
        <Box className="cc-js__danger">
          <Button variant="outlined" color="error" size="small" onClick={() => setConfirmDelete(true)}>
            Delete job seeker
          </Button>
          <span className="cc-js__danger-note">
            Permanent. Their applications are not deleted and will be left without a candidate.
          </span>
        </Box>
      </SectionCard>
    </>
  );

  /* ---- Preferences ---- */

  const preferences = (
    <>
      <SectionCard
        title="Job preferences"
        icon={<TuneOutlinedIcon />}
        editing={isEditing('preferences')}
        onEdit={() => startEdit('preferences')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField
          label="Job type"
          value={JOB_TYPES.find((t) => t.value === form.jobType)?.label || form.jobType}
          editing={isEditing('preferences')}
          edit={(
            <SelectFormField
              required={false}
              name="jobType"
              label=""
              value={form.jobType}
              options={JOB_TYPES}
              onChange={(_n, v) => setField('jobType', String(v))}
            />
          )}
        />
        <WorkspaceField label="Job category" value={form.jobCategory} editing={isEditing('preferences')} edit={input('jobCategory')} />
        <WorkspaceField label="Notice period" value={form.noticePeriod} editing={isEditing('preferences')} edit={input('noticePeriod')} />
        <WorkspaceField
          label="Notice (days)"
          value={form.noticePeriodDays ?? '-'}
          mono
          editing={isEditing('preferences')}
          edit={numberInput('noticePeriodDays', 'Whole days; 0 for immediate')}
        />

        <WorkspaceField label="Preferred locations" full value={null} />
        {chipEditor('location', 'preferences', cityDraft, setCityDraft, 'Add a city and press Enter')}

        <WorkspaceField label="Preferred roles" full value={null} />
        {chipEditor('preferredRoles', 'preferences', roleDraft, setRoleDraft, 'Add a role and press Enter')}
      </SectionCard>

      {/* Staff and the candidate only: the API redacts it for every other caller. */}
      <SectionCard
        title="Current pay"
        icon={<PaymentsOutlinedIcon />}
        editing={isEditing('currentPay')}
        onEdit={() => startEdit('currentPay')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField
          label="Amount"
          value={form.currentSalaryAmount?.toLocaleString() ?? '-'}
          mono
          editing={isEditing('currentPay')}
          edit={numberInput('currentSalaryAmount', 'Full amount, e.g. 600000')}
        />
        <WorkspaceField
          label="Currency"
          value={form.currentSalaryCurrency}
          mono
          editing={isEditing('currentPay')}
          edit={(
            <SelectFormField
              required={false}
              name="currentSalaryCurrency"
              label=""
              // WorkspaceField's label is a bare span with no link to this control, so name it here.
              selectProps={{ inputProps: { 'aria-label': 'Current pay currency' } }}
              value={form.currentSalaryCurrency}
              options={SALARY_CURRENCIES}
              onChange={(_n, v) => setField('currentSalaryCurrency', String(v))}
            />
          )}
        />
        <WorkspaceField
          label="Frequency"
          value={form.currentSalaryFrequency ? salaryFrequencyLabel(form.currentSalaryFrequency) : ''}
          editing={isEditing('currentPay')}
          edit={(
            <SelectFormField
              required={false}
              name="currentSalaryFrequency"
              label=""
              // WorkspaceField's label is a bare span with no link to this control, so name it here.
              selectProps={{ inputProps: { 'aria-label': 'Current pay frequency' } }}
              value={form.currentSalaryFrequency}
              options={SALARY_FREQUENCY_OPTIONS}
              onChange={(_n, v) => setField('currentSalaryFrequency', String(v))}
            />
          )}
        />
        <WorkspaceField label="As written" full value={form.currentSalary} />
      </SectionCard>

      <SectionCard
        title="Expected salary"
        icon={<BadgeOutlinedIcon />}
        editing={isEditing('salary')}
        onEdit={() => startEdit('salary')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField
          label="Minimum"
          value={form.salaryExpectationMin?.toLocaleString() ?? '-'}
          mono
          editing={isEditing('salary')}
          edit={numberInput('salaryExpectationMin', 'Full amount, e.g. 500000')}
        />
        <WorkspaceField
          label="Maximum"
          value={form.salaryExpectationMax?.toLocaleString() ?? '-'}
          mono
          editing={isEditing('salary')}
          edit={numberInput('salaryExpectationMax', 'Full amount, e.g. 800000')}
        />
        <WorkspaceField
          label="Currency"
          value={form.salaryExpectationCurrency}
          mono
          editing={isEditing('salary')}
          edit={(
            <SelectFormField
              required={false}
              name="salaryExpectationCurrency"
              label=""
              // WorkspaceField's label is a bare span with no link to this control, so name it here.
              selectProps={{ inputProps: { 'aria-label': 'Expected salary currency' } }}
              value={form.salaryExpectationCurrency}
              options={SALARY_CURRENCIES}
              onChange={(_n, v) => setField('salaryExpectationCurrency', String(v))}
            />
          )}
        />
        <WorkspaceField
          label="Frequency"
          value={form.salaryExpectationFrequency ? salaryFrequencyLabel(form.salaryExpectationFrequency) : ''}
          editing={isEditing('salary')}
          edit={(
            <SelectFormField
              required={false}
              name="salaryExpectationFrequency"
              label=""
              // WorkspaceField's label is a bare span with no link to this control, so name it here.
              selectProps={{ inputProps: { 'aria-label': 'Expected salary frequency' } }}
              value={form.salaryExpectationFrequency}
              options={SALARY_FREQUENCY_OPTIONS}
              onChange={(_n, v) => setField('salaryExpectationFrequency', String(v))}
            />
          )}
        />
        <WorkspaceField label="Note" full value={form.salaryExpectation} editing={isEditing('salary')} edit={input('salaryExpectation')} />
      </SectionCard>
    </>
  );

  /* ---- Skill profile ---- */

  const skills = (
    <>
      <SectionCard title="Mintly AI" icon={<PsychologyOutlinedIcon />}>
        <SkillProfilePanel jobSeekerId={form.id} />
      </SectionCard>

      <SectionCard
        title="Skills"
        icon={<PsychologyOutlinedIcon />}
        meta={`${(form.skills ?? []).filter(Boolean).length} skills`}
        editing={isEditing('skills')}
        onEdit={() => startEdit('skills')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        {chipEditor('skills', 'skills', skillDraft, setSkillDraft, 'Add a skill and press Enter')}
        <WorkspaceField
          label="Embedding"
          value={form.embedding?.vectorV2?.length === EMBEDDING_DIMS ? 'Indexed' : 'Not indexed'}
          mono
        />
      </SectionCard>
    </>
  );

  /* ---- Applications ---- */

  const since = Date.now() - APP_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const recentApps = applications.filter((a) => a.createdAt && new Date(a.createdAt).getTime() >= since);
  const pagedApps = recentApps.slice(appPage * APP_PAGE_SIZE, (appPage + 1) * APP_PAGE_SIZE);

  const applicationsTab = (
    <SectionCard
      title={`Applications (past ${APP_WINDOW_DAYS} days)`}
      icon={<WorkOutlineIcon />}
      meta={recentApps.length ? `${recentApps.length} total` : undefined}
    >
      {appsLoading ? (
        <Box sx={{ gridColumn: '1 / -1' }}><Loader /></Box>
      ) : recentApps.length === 0 ? (
        <WorkspaceField label="Applications" full value={`No applications in the past ${APP_WINDOW_DAYS} days.`} />
      ) : (
        <Box className="cc-js__apps">
          {pagedApps.map((a) => (
            <Box key={a._id} className="cc-js__app">
              <Box className="cc-js__app-copy">
                <span className="cc-js__app-title">{a.job?.title || '-'}</span>
                <span className="cc-js__app-company">
                  {a.job?.companyName || '-'}
                  {a.job?.location ? ` · ${a.job.location}` : ''}
                </span>
              </Box>
              <Box className="cc-js__app-meta">
                <CRMStatusChip status={a.status} kind="application" label={statusSummary(a.status, a.subStatus, a.closedReason)} />
                <span className="cc-js__app-date">
                  {a.createdAt ? fmt(a.createdAt) : '-'}
                </span>
                <IconButton
                  aria-label="Open application"
                  size="small"
                  onClick={() => navigate(`/job-applications/${a._id}`)}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
          {recentApps.length > APP_PAGE_SIZE && (
            <TablePagination
              component="div"
              count={recentApps.length}
              page={appPage}
              rowsPerPage={APP_PAGE_SIZE}
              rowsPerPageOptions={[]}
              onPageChange={(_, p) => setAppPage(p)}
            />
          )}
        </Box>
      )}
    </SectionCard>
  );

  /* ---- Documents ---- */

  const resumeName = resumeFile instanceof File
    ? resumeFile.name
    : form.resumeUrl?.split('/').pop() ?? '';

  const documents = (
    <SectionCard
      title="Resume"
      icon={<DescriptionOutlinedIcon />}
      editing={isEditing('resume')}
      onEdit={() => startEdit('resume')}
      onCancel={cancelEdit}
      onSave={saveSection}
    >
      {form.resumeUrl || resumeFile instanceof File ? (
        <Box className="cc-js__resume">
          <DescriptionOutlinedIcon className="cc-js__resume-icon" />
          <span className="cc-js__resume-name">{resumeName || 'Resume'}</span>
          <Box className="cc-js__resume-actions">
            {form.resumeUrl && (
              <>
                <Button size="small" variant="outlined" endIcon={<DownloadIcon />} onClick={downloadResume}>
                  Download
                </Button>
                <Button size="small" variant="outlined" onClick={openPreview} disabled={previewLoading}>
                  {previewLoading ? 'Loading…' : 'Preview'}
                </Button>
              </>
            )}
            {isEditing('resume') && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                endIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  setResumeFile('');
                  setField('resumeUrl', '');
                }}
              >
                Remove
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box className="cc-js__dropzone">
          <FileUpload
            disabled={!isEditing('resume')}
            file={resumeFile}
            maxFileSize={MAX_RESUME_BYTES}
            supportedFileTypes=".pdf"
            title={isEditing('resume') ? 'Upload resume' : 'No resume on file'}
            onFileDrop={(f) => {
              setResumeFile(f);
              setDirty(true);
            }}
          />
        </Box>
      )}
    </SectionCard>
  );

  /* ---- History ---- */

  const history = (
      <>
      <SectionCard title="Notes history" icon={<HistoryIcon />}>
        {(form.notes ?? []).length === 0 ? (
          <WorkspaceField label="History" value="No notes recorded" full />
        ) : (
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(form.notes ?? []).map((n, i) => (
              <WorkspaceField key={n.id ?? i} label={`${fmt(n.date)} · ${n.changedBy || 'Unknown'}`} value={n.note} full />
            ))}
          </Box>
        )}
      </SectionCard>
        <RecordActivityCard recordId={form.id} />
      </>
  );

  const tabContent: Record<string, React.ReactNode> = {
    profile,
    preferences,
    skills,
    applications: applicationsTab,
    documents,
    history,
  };

  const composer = (
    <NotesSection
      isEditMode
      notes={(form.notes ?? []).map((n, i) => ({
        id: n.id ?? String(i),
        note: n.note,
        changedBy: n.changedBy,
        date: n.date,
      }))}
      onAdd={addNote}
      onDelete={removeNote}
    />
  );

  const SECTION_LABELS: Record<string, string> = {
    personal: 'Personal details',
    address: 'Address',
    professional: 'Professional',
    preferences: 'Job preferences',
    salary: 'Expected salary',
    skills: 'Skills',
    resume: 'Resume',
  };

  return (
    <>
      <EntityWorkspace
        backLabel="Job Seekers"
        onBack={() => navigate('/jobseekers')}
        identityRail={identityRail}
        tabs={TABS}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        activityRail={<ActivityRail composer={composer} timeline={timeline} />}
        activityOpen={activityOpen}
        onToggleActivity={toggleActivity}
        isDirty={dirty && !!editing}
        dirtySectionLabel={editing ? SECTION_LABELS[editing] ?? 'Section' : undefined}
        onDiscard={cancelEdit}
        onSave={saveSection}
      >
        {tabContent[activeTab]}
      </EntityWorkspace>

      <RecordPickerDialog
        open={submitOpen}
        title={`Submit ${form.fullName || 'candidate'} to a demand`}
        searchPlaceholder="Search jobs by title or company"
        confirmLabel="Submit candidate"
        submitting={submitting}
        onSearch={searchJobs}
        onConfirm={doSubmitToJob}
        onClose={() => setSubmitOpen(false)}
      />

      {/* Full-screen because a resume is a page, not a thumbnail. This shows a
          document, not the record, so it is not the modal guardrail 9 rules out. */}
      <Dialog fullScreen open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box className="cc-js__preview-bar">
          <span className="cc-js__preview-name">{resumeName || 'Resume'}</span>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {previewUrl && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={inlineResumeUrl ?? previewUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in tab
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={previewUrl}
                  download
                >
                  Download
                </Button>
              </>
            )}
            <Button size="small" variant="contained" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </Box>
        </Box>
        {previewUrl && /\.pdf(\?|#|$)/i.test(previewUrl) ? (
          // Native browser render via a presigned inline URL (forces render, not download).
          inlineResumeUrl ? (
            <iframe className="cc-js__preview-frame" title="Resume preview" src={inlineResumeUrl} />
          ) : (
            <Box className="cc-js__preview-fallback">
              <span className="cc-js__preview-fallback-title">Loading preview…</span>
            </Box>
          )
        ) : (
          <Box className="cc-js__preview-fallback">
            <span className="cc-js__preview-fallback-title">
              {previewUrl ? "This file can't be previewed inline" : 'No resume on file'}
            </span>
            {previewUrl && (
              <>
                <span className="cc-js__preview-fallback-sub">
                  Word documents open in a new tab or download.
                </span>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    component="a"
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in a new tab
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    component="a"
                    href={previewUrl}
                    download
                  >
                    Download
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this job seeker?"
        body={(
          <>
            This removes {form.fullName || 'the candidate'} and their profile permanently.
            Their applications are not deleted and will be left without a candidate.
          </>
        )}
        // Email first: it is unique and is the identity key, while two people
        // can share a name and a Google sign-up leaves fullName blank. The
        // literal fallback keeps an email-less imported record deletable.
        confirmText={form.email || form.fullName || 'DELETE'}
        confirmLabel={form.email ? 'Email' : form.fullName ? 'Full name' : 'Type DELETE'}
        actionLabel="Delete job seeker"
        busy={saving}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default JobSeekerWorkspace;
